import type { FC } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { getAssetUrl } from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils';
import { FormattedMessage } from 'react-intl';
import {
    GiftRegistryWithHeartIcon,
    OrderHistoryIcon,
    PerksIcon,
    SavePaymentIcon,
} from '../../../../components/icons';
import { SoftLink } from '../../../../components/soft-link/SoftLink';
import { Button, Header, Text } from '../../../../ui-components';
import { PageUrl } from '../../../utils/page-url';

interface BenefitsProps {
    showCreateAccountLink?: boolean;
}

export const Benefits: FC<BenefitsProps> = ({ showCreateAccountLink }) => (
    <Flex
        alignItems="center"
        direction="column"
        gap={8}
        paddingY={16}
        backgroundImage={`url("${getAssetUrl('static/img/global/benefits-background.jpg')}")`}
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
        flex={1}
        data-testid="account-benefits-container"
        justifyContent="center"
    >
        <Box
            backgroundColor="white"
            borderRadius={{ base: 0, sm: '2xl' }}
            padding={4}
            width={{ base: '100%', sm: '448px' }}
        >
            <Flex direction="column" gap={4}>
                <Header
                    id="account-benefits-title"
                    as="h4"
                    style="bold"
                    variant="header4"
                    color="slt-pepper.1000"
                >
                    <FormattedMessage
                        id="account.benefits.title"
                        defaultMessage="Are you new here?"
                    />
                </Header>
                <Flex direction="column" gap={3}>
                    <Flex alignItems="center" gap={2}>
                        <Box
                            backgroundColor="slt-beige.300"
                            borderRadius="100px"
                            width={20}
                            height={20}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <PerksIcon data-testid="account-benefits-perks-icon" />
                        </Box>
                        <Flex direction="column" gap={1}>
                            <Text id="account-benefits-perks-title" style="bold">
                                <FormattedMessage
                                    id="account.benefits.perks.title"
                                    defaultMessage="Perks Rewards"
                                />
                            </Text>
                            <Text id="account-benefits-perks-description">
                                <FormattedMessage
                                    id="account.benefits.perks.description"
                                    defaultMessage="Earn Perks with every purchase"
                                />
                            </Text>
                        </Flex>
                    </Flex>

                    <Flex alignItems="center" gap={2} flex={1}>
                        <Box
                            backgroundColor="slt-beige.300"
                            borderRadius="100px"
                            width={20}
                            height={20}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <OrderHistoryIcon
                                data-testid="account-benefits-order-history-icon"
                                width="32px"
                                height="32px"
                            />
                        </Box>
                        <Flex direction="column" gap={1} flex={1}>
                            <Text id="account-benefits-order-history-title" style="bold">
                                <FormattedMessage
                                    id="account.benefits.order-history.title"
                                    defaultMessage="Order History"
                                />
                            </Text>
                            <Text id="account-benefits-order-history-description">
                                <FormattedMessage
                                    id="account.benefits.order-history.description"
                                    defaultMessage="Easily track and review orders"
                                />
                            </Text>
                        </Flex>
                    </Flex>

                    <Flex alignItems="center" gap={2}>
                        <Box
                            backgroundColor="slt-beige.300"
                            borderRadius="100px"
                            width={20}
                            height={20}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <SavePaymentIcon
                                data-testid="account-benefits-save-payment-icon"
                                width="32px"
                                height="32px"
                            />
                        </Box>
                        <Flex direction="column" gap={1} flex={1}>
                            <Text id="account-benefits-save-payment-title" style="bold">
                                <FormattedMessage
                                    id="account.benefits.save-payment.title"
                                    defaultMessage="Faster Checkout"
                                />
                            </Text>
                            <Text id="account-benefits-save-payment-description">
                                <FormattedMessage
                                    id="account.benefits.save-payment.description"
                                    defaultMessage="Save payment details for speedy checkout"
                                />
                            </Text>
                        </Flex>
                    </Flex>

                    <Flex alignItems="center" gap={2}>
                        <Box
                            backgroundColor="slt-beige.300"
                            borderRadius="100px"
                            width={20}
                            height={20}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <GiftRegistryWithHeartIcon
                                data-testid="account-benefits-gift-registry-icon"
                                width="32px"
                                height="32px"
                            />
                        </Box>
                        <Flex direction="column" gap={1} flex={1}>
                            <Text id="account-benefits-gift-registry-title" style="bold">
                                <FormattedMessage
                                    id="account.benefits.gift-registry.title"
                                    defaultMessage="Gift Registries"
                                />
                            </Text>
                            <Text id="account-benefits-gift-registry-description">
                                <FormattedMessage
                                    id="account.benefits.gift-registry.description"
                                    defaultMessage="Manage Gift Registries and Wishlists"
                                />
                            </Text>
                        </Flex>
                    </Flex>

                    {showCreateAccountLink ? (
                        <SoftLink
                            to={PageUrl.ACCOUNT_REGISTRATION}
                            className="mt-3 w-full"
                            data-testid="account-benefits-create-account-link"
                        >
                            <Button variant="secondary" wide>
                                <FormattedMessage
                                    id="account.benefits.create-account"
                                    defaultMessage="Create Account"
                                />
                            </Button>
                        </SoftLink>
                    ) : null}
                </Flex>
            </Flex>
        </Box>
    </Flex>
);

Benefits.defaultProps = {
    showCreateAccountLink: false,
};
